import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, HardHat, CheckCircle2, Phone, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useLanguage } from '../context/LanguageContext';
import { ObfuscatedPhone } from '../components/ObfuscatedPhone';
import { ObfuscatedBetonEmail } from '../components/ObfuscatedBetonEmail';

export default function Concrete() {
  const { language, getLocalizedLink } = useLanguage();

  // SEO Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Concrete Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Kenscho Design",
      "telephone": "+1-514-291-3737",
      "email": "Beton@Kenscho.ca",
      "address": { "@type": "PostalAddress", "addressRegion": "QC", "addressCountry": "CA" },
      "areaServed": ["Lanaudière", "Laurentians", "Laval", "Montreal"]
    },
    "name": language === 'fr' ? "Services de Béton" : "Concrete Services",
    "description": language === 'fr' 
      ? "Services de béton professionnels: coulage, coffrage, coupe, finition. Fondations, dalles, trottoirs."
      : "Professional concrete services: pouring, forming, cutting, finishing. Foundations, slabs, sidewalks."
  };

  const content = {
    en: {
      tagline: 'Kenscho Concrete',
      title1: 'Professional',
      title2: 'Concrete Services',
      desc: 'Quality concrete work for residential, commercial, and industrial projects. From forming to finishing, we handle every aspect of your concrete needs.',
      servicesTitle: 'Our Concrete Services',
      services: [
        { 
          title: 'Concrete Cutting', 
          desc: 'Precision cutting for openings, expansions, and modifications. Clean cuts for doors, windows, HVAC, and utility access.',
          features: ['Wall cutting', 'Floor cutting', 'Core drilling', 'Expansion joints']
        },
        { 
          title: 'Concrete Pouring', 
          desc: 'Expert concrete placement for foundations, slabs, driveways, and more. Proper techniques for lasting results.',
          features: ['Foundations', 'Slabs & floors', 'Driveways', 'Sidewalks & patios']
        },
        { 
          title: 'Concrete Forming', 
          desc: 'Custom formwork for walls, footings, and structural elements. Precise forms for clean, accurate pours.',
          features: ['Foundation walls', 'Footings', 'Columns & pillars', 'Retaining walls']
        },
        { 
          title: 'Concrete Finishing', 
          desc: 'Professional finishing for smooth, durable surfaces. Various textures and finishes available.',
          features: ['Smooth trowel finish', 'Broom finish', 'Exposed aggregate', 'Stamped concrete']
        }
      ],
      whyChoose: 'Why Choose Kenscho Concrete',
      whyDesc: 'We deliver quality concrete work that stands the test of time.',
      whyPoints: [
        'Experienced crew with years of concrete expertise',
        'Proper site preparation and reinforcement',
        'Quality materials and proven techniques',
        'Clean, professional workmanship',
        'Serving Lanaudière, Laurentians, Laval & Montreal'
      ],
      getQuote: 'Get a Free Quote',
      requestService: 'Request Service',
      concreteCta: 'Ready to get started?',
      concreteCtaDesc: 'Call now to schedule a consultation or get a quote for your concrete project.',
      concreteCtaButton: 'Get a Quote'
    },
    fr: {
      tagline: 'Kenscho Béton',
      title1: 'Services de',
      title2: 'béton professionnels',
      desc: 'Travaux de béton de qualité pour projets résidentiels, commerciaux et industriels. Du coffrage à la finition, nous gérons tous les aspects de vos besoins en béton.',
      servicesTitle: 'Nos services de béton',
      services: [
        { 
          title: 'Coupe de béton', 
          desc: 'Coupe de précision pour ouvertures, agrandissements et modifications. Coupes nettes pour portes, fenêtres, CVAC et accès aux services.',
          features: ['Coupe de murs', 'Coupe de plancher', 'Carottage', 'Joints de dilatation']
        },
        { 
          title: 'Coulée de béton', 
          desc: 'Placement expert du béton pour fondations, dalles, entrées et plus. Techniques appropriées pour des résultats durables.',
          features: ['Fondations', 'Dalles et planchers', 'Entrées', 'Trottoirs et patios']
        },
        { 
          title: 'Coffrage de béton', 
          desc: 'Coffrage sur mesure pour murs, semelles et éléments structurels. Formes précises pour des coulées nettes et exactes.',
          features: ['Murs de fondation', 'Semelles', 'Colonnes et piliers', 'Murs de soutènement']
        },
        { 
          title: 'Finition de béton', 
          desc: 'Finition professionnelle pour surfaces lisses et durables. Diverses textures et finitions disponibles.',
          features: ['Finition lisse', 'Finition balai', 'Agrégat exposé', 'Béton estampé']
        }
      ],
      whyChoose: 'Pourquoi choisir Kenscho Béton',
      whyDesc: 'Nous livrons des travaux de béton de qualité qui résistent à l\'épreuve du temps.',
      whyPoints: [
        'Équipe expérimentée avec des années d\'expertise en béton',
        'Préparation adéquate du site et renforcement',
        'Matériaux de qualité et techniques éprouvées',
        'Travail propre et professionnel',
        'Desservant Lanaudière, Laurentides, Laval et Montréal'
      ],
      getQuote: 'Obtenir un devis gratuit',
      requestService: 'Demander un service',
      concreteCta: 'Prêt à commencer?',
      concreteCtaDesc: 'Appelez maintenant pour planifier une consultation ou obtenir un devis pour votre projet de béton.',
      concreteCtaButton: 'Obtenir un devis'
    }
  };

  const t = content[language];

  return (
    <div className="grain-texture pt-20" data-testid="concrete-page">
      <Helmet>
        <title>{language === 'fr' 
          ? 'Services de Béton | Coulage, Coffrage, Finition | Lanaudière, Laurentides, Laval, Montréal | Kenscho'
          : 'Concrete Services | Pouring, Forming, Finishing | Lanaudière, Laurentians, Laval, Montreal | Kenscho'}
        </title>
        <meta name="description" content={language === 'fr'
          ? 'Services de béton professionnels à Lanaudière, Laurentides, Laval et Montréal. Coulage, coffrage, coupe, finition. Fondations, dalles, trottoirs. RBQ 5829 4596 01.'
          : 'Professional concrete services in Lanaudière, Laurentians, Laval, and Montreal. Pouring, forming, cutting, finishing. Foundations, slabs, sidewalks. RBQ 5829 4596 01.'} />
        <meta name="keywords" content={language === 'fr'
          ? 'béton, coulage béton, coffrage, finition béton, fondation, dalle, trottoir, Lanaudière, Laurentides, Laval, Montréal, béton estampé'
          : 'concrete, concrete pouring, forming, concrete finishing, foundation, slab, sidewalk, Lanaudière, Laurentians, Laval, Montreal, stamped concrete'} />
        <link rel="canonical" href={`https://www.kenscho.ca${getLocalizedLink('/concrete')}`} />
        
        <link rel="alternate" hreflang="en" href="https://www.kenscho.ca/concrete" />
        <link rel="alternate" hreflang="fr" href="https://www.kenscho.ca/fr/concrete" />
        <link rel="alternate" hreflang="x-default" href="https://www.kenscho.ca/concrete" />
        
        <meta property="og:title" content={language === 'fr' ? 'Services de Béton | Kenscho Design' : 'Concrete Services | Kenscho Design'} />
        <meta property="og:description" content={language === 'fr'
          ? 'Services de béton professionnels. Coulage, coffrage, finition. Lanaudière, Laurentides, Laval, Montréal.'
          : 'Professional concrete services. Pouring, forming, finishing. Lanaudière, Laurentians, Laval, Montreal.'} />
        <meta property="og:url" content={`https://www.kenscho.ca${getLocalizedLink('/concrete')}`} />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />
        
        <meta name="geo.region" content="CA-QC" />
        <meta name="geo.placename" content="Lanaudière, Laurentians, Laval, Montreal" />
        
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Hero */}
      <section className="py-20 bg-primary/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
              <HardHat className="h-6 w-6 text-white" />
            </div>
            <span className="text-sm font-medium tracking-widest uppercase text-primary animate-fade-in-up">
              {t.tagline}
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-light tracking-tight mb-6 animate-fade-in-up animation-delay-100">
            {t.title1} <span className="text-primary">{t.title2}</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl animate-fade-in-up animation-delay-200">
            {t.desc}
          </p>
        </div>
      </section>

      {/* CTA Banner */}
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="bg-primary/10 border border-primary/20 rounded-xl p-6 md:p-8">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
              <div>
                <h3 className="text-2xl font-light mb-2">{t.concreteCta}</h3>
                <p className="text-muted-foreground">{t.concreteCtaDesc}</p>
              </div>
              <div className="flex flex-col sm:flex-row items-center gap-6">
                <div className="flex items-center gap-6">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                    <Phone className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedPhone className="font-medium whitespace-nowrap" />
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-primary flex-shrink-0" />
                    <ObfuscatedBetonEmail className="font-medium" />
                  </div>
                </div>
                <Button asChild className="rounded-full btn-animate">
                  <Link to={getLocalizedLink('/contact')}>
                    {t.concreteCtaButton} <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h2 className="text-3xl font-light tracking-tight mb-12">
            {t.servicesTitle}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {t.services.map((service, index) => (
              <div key={index} className="service-card bg-card border border-border/60 p-8 rounded-lg stagger-item">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <HardHat className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-xl font-medium mb-3">{service.title}</h3>
                <p className="text-muted-foreground mb-6">{service.desc}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 bg-primary rounded-full shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-muted/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-light tracking-tight mb-8">
                {t.whyChoose}
              </h2>
              <p className="text-muted-foreground mb-8">{t.whyDesc}</p>
              
              <div className="space-y-4">
                {t.whyPoints.map((point, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                    <p className="text-muted-foreground">{point}</p>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Button asChild size="lg" className="rounded-full btn-animate" data-testid="concrete-cta-btn">
                  <Link to={getLocalizedLink('/contact')}>
                    {t.getQuote} <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="aspect-square rounded-lg overflow-hidden bg-muted">
              <img 
                src="https://images.unsplash.com/photo-1504307651254-35680f356dfd?w=800"
                alt="Concrete work"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
